import { estimateCastCheck, estimateContest, type EstimateRoll, type TargetSuccessEstimate } from '../shared/target-success-estimate';
import { estimateSkillAttackHitChance } from '../shared/ability-targeting-contract';
import { resolveCreatureSkillEntry } from '../janus-engine/display/creature-skill-entry-resolution';
import { inventoryField } from '../shared/array-types';
import { weaponAttackSkill } from '../janus-engine/actions/weapon-attack-skill';
import { findCarriedInventoryEntry } from '../janus-engine/inventory/find-inventory-entry';
import { currentFt3dSceneWindow } from '../ft3d-hud/scene-frame';
import { _ftCamField } from '../shared/campaign-field';
import { campaignHouseRules } from '../janus-engine/ability/house-rules';
import { record } from '../janus-engine/ability/context';
import type { AbilityTargetingProfile } from '../shared/ability-targeting-contract';

type Row = Record<string, any>;
type Draft = { lane: 'ability' | 'aoe'; version: number; casterId: string; abilityId: string; ids: string[]; facts: Row[];
  rollPrivate?: number;
  weaponAttack?: AbilityTargetingProfile['weaponAttack'] };
export interface SuccessPreview { lane: 'ability' | 'aoe'; version: number; casterId: string; abilityId: string; rollPrivate: number; estimates: TargetSuccessEstimate[] }
declare global { interface Window { ftTargetSuccessPreviews?: Partial<Record<'ability' | 'aoe', SuccessPreview>> } }
const essenceNames: Record<string, string> = {
  '22918408-67CC-4093-80E8-BF6FA8915C21': 'body',
  '63ABC96E-3804-4E1F-B38D-AABA3237B2D8': 'mind',
  '72F93730-134F-4CA2-8FC4-F2915F20CAC6': 'soul',
};
const number = (v: unknown): number | null => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v)) ? Number(v) : null;
const idOf = (r: any): string => String(typeof r === 'string' ? r : r?.item_layer_effect ?? r?.item_id ?? r?.value ?? r?.[0] ?? '').toUpperCase();
const layers = (r: unknown, key: string): string[] => ftLayerEntries(r, key).map(idOf).filter(Boolean);
function cachedActor(id: string): Row | null {
  const w = window as Window & { ft3dEncounterArrCache?: { arr?: Row } };
  const rows = w.ft3dEncounterArrCache?.arr?.encounter_creatures_arr;
  return window.FateTide?.casterCache?.get(id) as Row ?? (Array.isArray(rows) ? rows.find((r: Row) =>
    idOf(r.encounter_creatures?.encounter_creature_id ?? r.encounter_creature_id) === id) : null) ?? null;
}
function skillRoll(actor: Row, id: string): EstimateRoll | null {
  const cat = window.SKILL_CATALOG?.[id];
  if (!cat?.variable_name) return null;
  const definitions = Object.entries(window.SKILL_CATALOG ?? {}).map(([item_id, skill]) => ({ ...skill, item_id }));
  const entry = resolveCreatureSkillEntry({ canonicalSkills: actor.computed?.creature?.skills ?? null, legacySkills: null,
    variableName: cat.variable_name, registry: {
      byId: Object.fromEntries(definitions.map(s => [s.item_id, s])),
      byVariable: Object.fromEntries(definitions.map(s => [s.variable_name ?? '', s])),
    } }) as Row | null;
  if (!entry) return null; // Missing projected information is not an assumed zero defense.
  const essence = cat.essence_used === 'spellcasting_essence'
    ? number(actor.computed?.creature?.spellcasting?.essence_mod ?? ftLayerValue(actor, 'spellcasting_essence_mod'))
    : number(actor.computed?.creature?.essences?.[essenceNames[String(cat.essence_used).toUpperCase()] ?? '']?.total);
  if (essence === null) return null;
  const tier = number(entry.tier_level) ?? 0;
  return { tierDie: tier > 0 ? number(entry.roll?.die) ?? tier + 3 : 0,
    modifier: essence + (number(entry.mod) ?? (number(entry.skill_roll_mod) ?? 0) + (number(entry.skill_rolls_mod) ?? 0)),
    stacks: (number(entry.adv_dis_stacks) ?? 0) + (entry.is_flawed === true || tier === 0 ? -1 : 0),
    forcedNatural: number(entry.forced_natural_main_roll) ?? undefined };
}
function bestSkill(actor: Row, ids: string[]): { id: string; roll: EstimateRoll } | null {
  let best: { id: string; roll: EstimateRoll; score: number } | null = null;
  for (const id of ids) {
    const roll = skillRoll(actor, id); if (!roll) return null;
    const adv = roll.stacks ? Math.sign(roll.stacks) * (3.5 + (Math.abs(roll.stacks) - 1) * 1.6) : 0;
    const score = 10.5 + roll.modifier + (roll.tierDie ? (roll.tierDie + 1) / 2 : 0) + adv;
    if (!best || score > best.score) best = { id, roll, score };
  }
  return best;
}
function armorCastCheck(caster: Row, ability: Row): { percent: number | null } | null {
  if (ability.items?.item_type !== 'spell') return null;
  const spheres = layers(ability, 'spell_sphere');
  if (!spheres.length || spheres.includes('57F117E8-6659-4F2D-AED2-82D6005492B9')
    || !(ftLayerHas(ability, 'spell_component_somatic') || ftLayerHas(ability, 'spell_material_item_id'))) return null;
  const armor = ftLayerEntries(caster, 'inventory').find(row =>
    idOf(inventoryField(row, 'equipped')) === '0B279A55-5102-D704-8A57-0C1FF4989DFA');
  if (!armor) return null;
  const skills: Record<string, string> = {
    'D33CD865-4B66-4843-9AE5-9AFCA1133253': 'light_armor_skill',
    'FEC16F4D-6309-48A5-82E4-833EF06A0E54': 'medium_armor_skill',
    'F94A6EAD-BC5A-466D-B27B-7688423B4332': 'heavy_armor_skill',
  };
  const variable = skills[idOf(ftLayerValue(armor, 'armor_type'))];
  const id = Object.keys(window.SKILL_CATALOG ?? {}).find(key => variable && window.SKILL_CATALOG?.[key]?.variable_name === variable);
  const roll = id ? skillRoll(caster, id) : null;
  const ac = ftLayerHas(armor, 'ac_mod') ? number(ftLayerValue(armor, 'ac_mod')) : 0;
  return { percent: roll && ac !== null ? estimateCastCheck(roll, 5 + Math.trunc(ac)) : null };
}
/** Reads only the caster client's existing snapshots. Never fetches private target stats for odds. */
export function buildSuccessPreview(draft: Draft): SuccessPreview {
  const policy = document.querySelector<HTMLElement>('[data-ft3d-campaign-encounter-view]');
  const canSeeHidden = policy?.dataset.ft3dCanManage === '1';
  const context = typeof getSidelogContext === 'function' ? getSidelogContext() : null;
  const campaignId = idOf(policy?.dataset.campaignId ?? context?.campaign_id);
  const ft = window.FateTide;
  const campaign = [ft?.campaignCache?.get(campaignId), ft?.currentCampaignArr].map(record).find(row =>
    campaignId && idOf(_ftCamField(row, 'campaign_id')) === campaignId);
  const unhideMeta = campaignHouseRules(campaign)?.unhideMetaRolls === true;
  const rollPrivate = draft.rollPrivate === undefined ? Number(context?.roll_private ?? 0)
    : [0, 1, 2].includes(draft.rollPrivate) ? draft.rollPrivate : 1;
  const caster = cachedActor(draft.casterId);
  const ability = caster ? window.ftFindFullNestedItem?.(draft.abilityId) as Row
    ?? caster.catalog?.[draft.abilityId] ?? (window.FateTide?.objectCache as Map<string, Row> | undefined)?.get(draft.abilityId) as Row : null;
  const catalog = window.SKILL_CATALOG ?? {};
  const skill = catalog[draft.abilityId];
  const defenders = skill?.contest_target ? [skill.contest_target.toUpperCase()] : ability ? layers(ability, 'skill_roll_contestee') : [];
  const bending = Object.keys(catalog).find(id => catalog[id]?.variable_name === 'bending_weapon_skill');
  const attackers = skill ? [draft.abilityId] : ability ? layers(ability, 'skill_roll_contester') : [];
  if (!attackers.length && bending) attackers.push(bending);
  const fixedDC = ability ? number(ftLayerValue(ability, 'ability_dc')) : null;
  const isContest = defenders.length > 0;
  const autoHidden = isContest && !unhideMeta && [...defenders, ...(fixedDC === null ? attackers : [])].some(id => {
    const s = catalog[id];
    return s?.variable_name !== 'bending_weapon_skill' && ['mind', 'soul'].includes(essenceNames[String(s?.essence_used).toUpperCase()] ?? '');
  });
  const hidden = !canSeeHidden && (rollPrivate !== 0 || autoHidden);
  const rollType = ability ? String(ftLayerValue(ability, 'roll_type') ?? '').toUpperCase() : '';
  const attackKinds: Record<string, string> = {
    'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C': 'bending_weapon_skill',
    '5740CA49-006E-A7A0-B46B-6DDED6CD546C': 'bending_weapon_skill',
  };
  let attackSkill = attackKinds[rollType];
  if (ability && caster && (draft.weaponAttack || ['3F85CA97-F9B2-A83A-FDE8-E523FD99346C', '2FE8DFB7-FE61-4862-AF71-3523262350A0'].includes(rollType))) {
    const weapon = draft.weaponAttack ? findCarriedInventoryEntry(caster, draft.weaponAttack.inventoryId,
      { entries: ftLayerEntries, inventoryField: ftInventoryField }) : ability;
    if (weapon) attackSkill = weaponAttackSkill(weapon as Row, caster, { value: ftLayerValue, entries: ftLayerEntries }, draft.weaponAttack?.throwing);
  }
  const cast = caster && ability ? armorCastCheck(caster, ability) : null;
  // Both rows must fit the shared 256-entry preview budget.
  const targetIds = draft.ids.slice(0, cast && (isContest || attackSkill) ? 128 : 256);
  const estimates: TargetSuccessEstimate[] = (isContest || attackSkill) ? targetIds.map(entityId => {
    const kind = isContest ? 'contest' as const : 'hit' as const;
    if (hidden) return { entityId, kind, state: 'hidden' as const };
    const target = cachedActor(entityId);
    let percent: number | null = null;
    const fact = draft.facts.find(f => f.entityId === entityId);
    if (caster && target) {
      if (isContest) {
        const defending = bestSkill(target, defenders);
        const attacking = fixedDC ?? bestSkill(caster, attackers)?.roll ?? null;
        if (defending && attacking !== null) {
          const defense = { ...defending.roll };
          if (catalog[defending.id]?.variable_name === 'balance' && ['partial', 'heavy'].includes(fact?.coverTier)) defense.stacks++;
          if (ability?.items?.item_type === 'spell' && ftLayerValue(target, 'contesting_roll_adv_vs_spells')) defense.stacks++;
          percent = fact?.lineOfEffect === 'blocked' ? 0 : estimateContest(attacking, defense);
        }
      } else {
        const id = Object.keys(catalog).find(id => catalog[id]?.variable_name === attackSkill);
        const roll = id ? skillRoll(caster, id) : null;
        const defense = number(ftLayerValue(target, 'ec_total'));
        if (roll && defense !== null) percent = estimateSkillAttackHitChance({ tierDie: roll.tierDie,
          flatModifier: roll.modifier + (number(ftLayerValue(ability, 'weapon_attack_mod')) ?? 0),
          advDisStacks: roll.stacks + (fact?.flanked ? 1 : 0), defense });
      }
    }
    return percent === null ? { entityId, kind, state: 'unknown' as const } : { entityId, kind, state: 'estimated' as const, percent };
  }) : [];
  if (cast) for (const entityId of targetIds) {
    estimates.push(hidden ? { entityId, kind: 'cast', state: 'hidden' }
      : cast.percent === null ? { entityId, kind: 'cast', state: 'unknown' }
      : { entityId, kind: 'cast', state: 'estimated', percent: cast.percent });
  }
  return { lane: draft.lane, version: draft.version, casterId: draft.casterId, abilityId: draft.abilityId, rollPrivate, estimates };
}

const drafts: Partial<Record<'ability' | 'aoe', Draft>> = {};
// Stateless window bridge: observers use delivered snapshots without importing this bundle's drafts.
window.ftBuildTargetSuccessPreview = buildSuccessPreview;
const signatures = new Map<string, string>();
function sync(): void {
  const scene = currentFt3dSceneWindow();
  if (!scene) return;
  for (const lane of ['ability', 'aoe'] as const) {
    const draft = drafts[lane]; if (!draft) continue;
    const preview = buildSuccessPreview(draft);
    const signature = JSON.stringify(preview); if (signature === signatures.get(lane)) continue;
    signatures.set(lane, signature);
    (window.ftTargetSuccessPreviews ??= {})[lane] = preview;
    scene.postMessage({ type: 'ft3d-campaign-success-estimates-v1', payload: preview }, location.origin);
    window.dispatchEvent(new CustomEvent('ft:target-success-estimates', { detail: preview }));
  }
}
for (const [eventName, lane] of [['ft:ability-targeting-intent', 'ability'], ['ft:ft3d-aoe-placement-intent', 'aoe']] as const) {
  window.addEventListener(eventName, (event: Event) => {
    const envelope = (event as CustomEvent).detail;
    const intent = envelope?.intent;
    signatures.delete(lane);
    if (!intent) { delete drafts[lane]; if (window.ftTargetSuccessPreviews) delete window.ftTargetSuccessPreviews[lane]; return; }
    const version = lane === 'ability' ? intent.intentId : envelope.version;
    const previous = drafts[lane]?.version === version ? drafts[lane] : null;
    drafts[lane] = { lane, version,
      casterId: intent.casterId, abilityId: intent.abilityId,
      weaponAttack: intent.profile?.weaponAttack,
      ids: intent.selectedEntityIds?.length ? intent.selectedEntityIds : previous?.ids ?? [], facts: previous?.facts ?? [] };
    sync();
  });
}
window.addEventListener('message', event => {
  const scene = currentFt3dSceneWindow();
  if (!scene || event.origin !== location.origin || event.source !== scene) return;
  const { type, payload } = event.data ?? {};
  if (!payload || typeof payload !== 'object') return;
  if (type === 'ft3d-campaign-aoe-placement-stage-v1' && drafts.aoe && payload.version === drafts.aoe.version) drafts.aoe.ids = payload.entityIds ?? [];
  else if (type === 'ft3d-campaign-ability-targeting-preview-stage-v1' && drafts.ability && payload.casterId === drafts.ability.casterId) drafts.ability.ids = payload.targetId ? [payload.targetId] : [];
  else if (type === 'ft3d-campaign-ability-targeting-candidates-v1' && drafts.ability && payload.version === drafts.ability.version) drafts.ability.facts = payload.candidates ?? [];
  else return;
  sync();
});
window.addEventListener('ft:target-success-candidates', (event: Event) => {
  const payload = (event as CustomEvent).detail;
  if (drafts.ability && payload?.version === drafts.ability.version) { drafts.ability.facts = payload.candidates ?? []; sync(); }
});
document.addEventListener('load', event => {
  if ((event.target as HTMLElement)?.matches?.('[data-ft3d-encounter-frame]')) { signatures.clear(); sync(); }
}, true);
// Recompute from current projected snapshots/options without fetching or rolling. Idle is inert.
window.setInterval(() => { if (drafts.ability || drafts.aoe) sync(); }, 500);
